import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useUserStore } from "@/stores/user";
import * as RequestService from "@/services/RequestService";
import { useRequestsStore } from "@/stores/requests";

export const useRequestSurveyStore = defineStore("requestSurvey", () => {
  const activeUserRequestSurveyId = ref();
  const surveyDetails = ref({});
  const surveyQuestions = ref([]);
  const surveyEdits = ref({});
  const surveyComplete = ref(false);
  const requestDrawerOpen = ref(false);
  const categoryDrawerOpen = ref(false);
  const categoryDrawerMode = ref("custom");
  const loading = ref(false);
  const loadingTimeout = ref(null);
  const userStore = useUserStore();
  const firstRequest = ref(false);
  const requestsStore = useRequestsStore();

  const delayedSetLoading = () => {
    if (loading.value || loadingTimeout.value) {
      return;
    }

    loadingTimeout.value = setTimeout(() => {
      loading.value = true;
    }, 250);
  };

  const clearLoading = () => {
    clearTimeout(loadingTimeout.value);
    loadingTimeout.value = null;
    loading.value = false;
  };

  function resetSurveyState() {
    activeUserRequestSurveyId.value = null;
    surveyDetails.value = {};
    surveyQuestions.value = [];
    surveyEdits.value = {};
    surveyComplete.value = false;
  }

  function openCategoryDrawer(mode = "custom") {
    categoryDrawerOpen.value = true;
    categoryDrawerMode.value = mode;
  }

  async function beginRequestSurvey(requestSurveyDetails) {
    resetSurveyState();
    try {
      firstRequest.value = userStore.user.request_count === 0;
      requestDrawerOpen.value = true;
      delayedSetLoading();
      const requestSurvey = await RequestService.beginRequestSurvey(
        requestSurveyDetails.id,
      );
      surveyDetails.value = requestSurveyDetails;
      activeUserRequestSurveyId.value = requestSurvey.id;
      await getNextQuestion();
      return requestSurvey;
    } catch (error) {
      console.error(error);
    }
  }

  async function getNextQuestion() {
    if (!activeUserRequestSurveyId.value) {
      // This should never happen
      return;
    }

    delayedSetLoading();
    const nextQuestion = await RequestService.getNextQuestion(
      activeUserRequestSurveyId.value,
    );
    clearLoading();

    // Special case to pre-populate the first question asking for request name
    if (
      nextQuestion.label.trim() === "What's the name of your request?" &&
      surveyDetails.value.name.trim() !== "Start Custom Request"
    ) {
      nextQuestion.initialValue = surveyDetails.value.name;
    }

    surveyQuestions.value.push(nextQuestion);
  }

  function updateSurveyQuestionResponse(userResponse) {
    const existingQuestion = surveyQuestions.value.find(
      (question) => question.id === userResponse.questionId,
    );
    if (existingQuestion) {
      existingQuestion.answer = userResponse.answer;
      existingQuestion.customAnswerDisplay = userResponse.customAnswerDisplay;
      existingQuestion.editing = false;
      existingQuestion.skipped = existingQuestion.answer === null;
      if (existingQuestion.label.trim() === "What's the name of your request?") {
        surveyDetails.value.name = userResponse.answer;
      }
    }
  }

  async function answerQuestion(userResponse, isEdit) {
    try {
      updateSurveyQuestionResponse(userResponse);
      if (!isEdit) {
        delayedSetLoading();
      }
      const response = await RequestService.submitAnswer(
        activeUserRequestSurveyId.value,
        userResponse,
      );

      if (!isEdit && !userResponse.finalQuestion) {
        await getNextQuestion();
      }

      if (userResponse.finalQuestion) {
        clearLoading();
      }

      return response;
    } catch (error) {
      console.error(error);
      return error;
    }
  }

  async function completeSurvey() {
    try {
      await RequestService.completeSurvey(activeUserRequestSurveyId.value);
      await requestsStore.refreshOpenRequests();
      userStore.incrementRequestCount();
    } catch (error) {
      console.error(error);
    }
  }

  return {
    activeUserRequestSurveyId,
    surveyDetails,
    surveyQuestions,
    surveyEdits,
    requestDrawerOpen,
    loading,
    isFirstRequest: computed(() => firstRequest.value),
    categoryDrawerOpen,
    categoryDrawerMode,
    openCategoryDrawer,
    beginRequestSurvey,
    getNextQuestion,
    answerQuestion,
    completeSurvey,
  };
});
